export const bynderImageUrl = (field: string) => `
  coalesce(
    ${field}.selectedUrl,
    ${field}.url,
    ${field}.datUrl,
    ${field}.previewUrl,
    ${field}.previewImg
  )
`;

export const compactBynderImageProjection = (field: string) => `
  select(
    defined(${field}) => {
      "_type": "bynder.asset",
      "id": ${field}.id,
      "name": ${field}.name,
      "description": ${field}.description,
      "alt": coalesce(${field}.textMetaproperties[name == 'Alt_text'][0].value, ${field}.alt),
      "isSvg": ${field}.extensions[0] == "svg",
      "selectedUrl": ${field}.selectedUrl,
      "url": coalesce(${field}.url, ${field}.selectedUrl, ${field}.datUrl, ${field}.previewUrl, ${field}.previewImg),
      "width": ${field}.width,
      "height": ${field}.height,
      "aspectRatio": ${field}.aspectRatio
    },
    null
  )
`;

export const computedInternalUrl = `
  select(
    defined(parentPage->parentPage->slug.current) => "/products/" + parentPage->parentPage->slug.current + "/" + parentPage->slug.current + "/" + slug.current,
    defined(parentPage->slug.current) => "/products/" + parentPage->slug.current + "/" + slug.current,
    _type == "productsList" => "/products/" + category->slug.current + "/" + slug.current,
    _type == "product" && defined(subcategory->parentCategory->slug.current) => "/products/" + subcategory->parentCategory->slug.current + "/" + subcategory->slug.current + "/" + slug.current,
    _type == "structaProductLevel1" => "/products/" + slug.current,
    _type == "articlesList" => "/" + select(
      type == "blogs" => "blog",
      type
    ),
    _type == "article" && defined(type) && type == "blogs" => "/blog/" + slug.current,
    _type == "article" && defined(type) => "/" + type + "/" + slug.current,
    _type == "documentPage" => "/documents/" + slug.current,
    _type == "redRec.quoteSettings" => "/quote",
    _type == "faqs" => "/faq",
    _type == "contactUs" => "/contact-us",
    _type == "storeLocator" => "/store-locator",
    _type == "homePage" => "/",
    "/" + slug.current
  )
`;

export const internalReferenceUrl = `
  select(
    type == "internal" =>
      internalReference->{
        "computedUrl": ${computedInternalUrl}
      }.computedUrl,
    url
  )
`;

export const directInternalReferenceUrl = `
  select(
    link.type == "internal" =>
      link.internalReference->{
        "computedUrl": ${computedInternalUrl}
      }.computedUrl,
    link.type == "external" => link.url
  )
`;

export const LOGO_PROJECTTION = `
  logo{
    ...,
    link{
      ...,
      "url": ${internalReferenceUrl},
    },
    "bynderLogo": ${compactBynderImageProjection('bynderLogo')},
    "imageUrl": ${bynderImageUrl('bynderLogo')},
  }
`;

export const LOGO_PROJECTION = LOGO_PROJECTTION;

/**
 * IMPORTANT: every projection helper below must be a single template literal.
 *
 * Sanity typegen statically evaluates these functions to derive query result types.
 * Its evaluator supports only an expression-bodied arrow function whose body is one
 * template literal - no block bodies, no `if`, no ternaries, no local `const`s.
 * It also cannot follow an *aliased* import (`import { x as y }`) or a re-export across
 * workspace packages, so app query files import these unaliased and rebind them locally.
 *
 * Breaking any of those rules silently drops the generated types for every query that
 * uses the helper.
 */

/** Bynder-only image projection, shaped like a Sanity image for the components. */
export const bynderImageProjection = (
  alias: string = 'image',
  bynderField: string = 'bynderImage',
  alt: string = `coalesce(${bynderField}.textMetaproperties[name == 'Alt_text'][0].value, ${bynderField}.alt, ${bynderField}.description, ${bynderField}.name)`,
  title: string = `${bynderField}.name`,
) => `
    "${alias}": select(
      defined(${bynderField}) => {
        "_type": "image",
        "asset": {
          "_id": ${bynderField}.id,
          "_type": "reference",
          "url": coalesce(${bynderField}.selectedUrl, ${bynderField}.url, ${bynderField}.datUrl, ${bynderField}.previewUrl, ${bynderField}.previewImg),
          "metadata": {
            "dimensions": {
              "width": ${bynderField}.width,
              "height": ${bynderField}.height,
              "aspectRatio": ${bynderField}.aspectRatio
            }
          }
        },
        "alt": ${alt},
        "title": ${title},
        "url": coalesce(${bynderField}.selectedUrl, ${bynderField}.url, ${bynderField}.datUrl, ${bynderField}.previewUrl, ${bynderField}.previewImg),
        "width": ${bynderField}.width,
        "height": ${bynderField}.height,
        "bynderImage": select(
          defined(${bynderField}) => {
            "_type": "bynder.asset",
            "id": ${bynderField}.id,
            "name": ${bynderField}.name,
            "description": ${bynderField}.description,
            "alt": coalesce(${bynderField}.textMetaproperties[name == 'Alt_text'][0].value, ${bynderField}.alt),
            "isSvg": ${bynderField}.extensions[0] == "svg",
            "selectedUrl": ${bynderField}.selectedUrl,
            "url": coalesce(${bynderField}.url, ${bynderField}.selectedUrl, ${bynderField}.datUrl, ${bynderField}.previewUrl, ${bynderField}.previewImg),
            "width": ${bynderField}.width,
            "height": ${bynderField}.height,
            "aspectRatio": ${bynderField}.aspectRatio
          },
          null
        )
      },
      null
    )
  `;

/** As {@link bynderImageProjection}, but spread into the parent instead of aliased. */
export const spreadBynderImageProjection = (
  bynderField: string = 'bynderImage',
  alt: string = `coalesce(${bynderField}.textMetaproperties[name == 'Alt_text'][0].value, ${bynderField}.alt, ${bynderField}.description, ${bynderField}.name)`,
  title: string = `${bynderField}.name`,
) => `
    ...select(
      defined(${bynderField}) => {
        "_type": "image",
        "asset": {
          "_id": ${bynderField}.id,
          "_type": "reference",
          "url": coalesce(${bynderField}.selectedUrl, ${bynderField}.url, ${bynderField}.datUrl, ${bynderField}.previewUrl, ${bynderField}.previewImg),
          "metadata": {
            "dimensions": {
              "width": ${bynderField}.width,
              "height": ${bynderField}.height,
              "aspectRatio": ${bynderField}.aspectRatio
            }
          }
        },
        "alt": ${alt},
        "title": ${title},
        "url": coalesce(${bynderField}.selectedUrl, ${bynderField}.url, ${bynderField}.datUrl, ${bynderField}.previewUrl, ${bynderField}.previewImg),
        "width": ${bynderField}.width,
        "height": ${bynderField}.height,
        "bynderImage": select(
          defined(${bynderField}) => {
            "_type": "bynder.asset",
            "id": ${bynderField}.id,
            "name": ${bynderField}.name,
            "description": ${bynderField}.description,
            "alt": coalesce(${bynderField}.textMetaproperties[name == 'Alt_text'][0].value, ${bynderField}.alt),
            "isSvg": ${bynderField}.extensions[0] == "svg",
            "selectedUrl": ${bynderField}.selectedUrl,
            "url": coalesce(${bynderField}.url, ${bynderField}.selectedUrl, ${bynderField}.datUrl, ${bynderField}.previewUrl, ${bynderField}.previewImg),
            "width": ${bynderField}.width,
            "height": ${bynderField}.height,
            "aspectRatio": ${bynderField}.aspectRatio
          },
          null
        )
      },
      null
    )
  `;

/**
 * Coalesces a legacy Sanity image field with its Bynder sibling.
 * Only for the few fields that still have both - see docs/media-and-search.md.
 */
export const compatibleImageProjection = (
  alias: string = 'image',
  imageField: string = alias,
  bynderField: string = 'bynderImage',
  alt: string = `coalesce(${bynderField}.textMetaproperties[name == 'Alt_text'][0].value, ${bynderField}.alt, ${imageField}.alt, ${imageField}.asset->altText)`,
  title: string = `coalesce(${imageField}.title, ${imageField}.asset->title, ${imageField}.asset.title, ${bynderField}.name)`,
) => `
    "${alias}": select(
      defined(${imageField}) || defined(${bynderField}) => {
        "_type": coalesce(${imageField}._type, "image"),
        "asset": {
          "_id": coalesce(${bynderField}.id, ${imageField}.asset->_id, ${imageField}.asset._id),
          "_ref": ${imageField}.asset._ref,
          "_type": coalesce(${imageField}.asset->_type, ${imageField}.asset._type, "reference"),
          "url": coalesce(
            ${bynderField}.selectedUrl,
            ${bynderField}.url,
            ${bynderField}.datUrl,
            ${bynderField}.previewUrl,
            ${bynderField}.previewImg,
            ${imageField}.asset->url,
            ${imageField}.asset.url
          ),
          "metadata": {
            "lqip": ${imageField}.asset->metadata.lqip,
            "dimensions": {
              "width": coalesce(${bynderField}.width, ${imageField}.asset->metadata.dimensions.width),
              "height": coalesce(${bynderField}.height, ${imageField}.asset->metadata.dimensions.height),
              "aspectRatio": coalesce(${bynderField}.aspectRatio, ${imageField}.asset->metadata.dimensions.aspectRatio)
            }
          }
        },
        "crop": ${imageField}.crop,
        "hotspot": ${imageField}.hotspot,
        "alt": ${alt},
        "title": ${title},
        "url": coalesce(
          ${bynderField}.selectedUrl,
          ${bynderField}.url,
          ${bynderField}.datUrl,
          ${bynderField}.previewUrl,
          ${bynderField}.previewImg,
          ${imageField}.url,
          ${imageField}.asset->url,
          ${imageField}.asset.url
        ),
        "width": coalesce(${bynderField}.width, ${imageField}.asset->metadata.dimensions.width),
        "height": coalesce(${bynderField}.height, ${imageField}.asset->metadata.dimensions.height),
        "bynderImage": select(
          defined(${bynderField}) => {
            "_type": "bynder.asset",
            "id": ${bynderField}.id,
            "name": ${bynderField}.name,
            "description": ${bynderField}.description,
            "alt": coalesce(${bynderField}.textMetaproperties[name == 'Alt_text'][0].value, ${bynderField}.alt),
            "isSvg": ${bynderField}.extensions[0] == "svg",
            "selectedUrl": ${bynderField}.selectedUrl,
            "url": coalesce(${bynderField}.url, ${bynderField}.selectedUrl, ${bynderField}.datUrl, ${bynderField}.previewUrl, ${bynderField}.previewImg),
            "width": ${bynderField}.width,
            "height": ${bynderField}.height,
            "aspectRatio": ${bynderField}.aspectRatio
          },
          null
        )
      },
      null
    )
  `;
